import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, FormattedNumber, useIntl } from 'dibs-react-intl';
import { SbSharedMarketTrendsPriceStatsDescription } from './SbSharedMarketTrendsPriceStatsDescription.tsx';
import { SbSharedMarketTrendsQuestionWrapper } from './SbSharedMarketTrendsQuestionWrapper.tsx';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { getPriceByCurrency } from './currencyHelpers.ts';
import { priceStatsMessages } from './sharedMessages.ts';

import { useCurrency } from '../../shared/hooks/useCurrency.ts';

import dibsCss from 'dibs-css';
import cn from 'classnames';
import styles from './SbSharedMarketTrendsPriceStats.module.css';

import { type SbSharedMarketTrendsPriceStats_creatorMarketTrends$key } from './__generated__/SbSharedMarketTrendsPriceStats_creatorMarketTrends.graphql.ts';
import { SbSharedMarketTrendsIconicDesigns } from './SbSharedMarketTrendsIconicDesigns.tsx';

const creatorMarketTrendsFragment = graphql`
    fragment SbSharedMarketTrendsPriceStats_creatorMarketTrends on MarketTrendsConnection {
        ...SbSharedMarketTrendsIconicDesigns_creatorMarketTrends
        priceStats {
            itemsSold
            valueChange
            avgSalePrice {
                ...currencyHelpers_financialAmount
            }
            priceRange {
                low {
                    ...currencyHelpers_financialAmount
                }
                high {
                    ...currencyHelpers_financialAmount
                }
            }
        }
    }
`;

type Props = {
    creatorMarketTrends: SbSharedMarketTrendsPriceStats_creatorMarketTrends$key;
    topic: string;
};

export const SbSharedMarketTrendsPriceStats: FC<Props> = ({
    creatorMarketTrends: creatorMarketTrendsRef,
    topic,
}) => {
    const creatorMarketTrends =
        useFragment(creatorMarketTrendsFragment, creatorMarketTrendsRef) || {};
    const { priceStats } = creatorMarketTrends || {};
    const { isDesktop } = useServerVarsContext();
    const { currency } = useCurrency();
    const intl = useIntl();

    if (!priceStats) {
        return null;
    }

    const { itemsSold, valueChange, avgSalePrice, priceRange } = priceStats || {};

    let countWord = priceStatsMessages.dozens;
    if (itemsSold && itemsSold >= 100) {
        countWord = itemsSold >= 1000 ? priceStatsMessages.thousands : priceStatsMessages.hundreds;
    }

    const avgSalePriceAmount = getPriceByCurrency(avgSalePrice, currency) ?? 0;
    const priceRangeLowAmount = getPriceByCurrency(priceRange?.low, currency) ?? 0;
    const priceRangeHighAmount = getPriceByCurrency(priceRange?.high, currency) ?? 0;

    return (
        <SbSharedMarketTrendsQuestionWrapper
            question={intl.formatMessage(priceStatsMessages.question, { topic })}
            hasTopBorder={!!isDesktop}
        >
            <>
                <p
                    className={cn(
                        dibsCss.sassyFontBodyMedium,
                        isDesktop ? dibsCss.mbMedium : dibsCss.mbSmall
                    )}
                >
                    <FormattedMessage
                        id="abf.SbSharedMarketTrends.priceStats.description"
                        defaultMessage="Based on an analysis of <bold>{countWord}</bold> of sales on 1stDibs, the price for {topic} can vary depending on condition, rarity, and materials. Here is a detailed breakdown of current market pricing."
                        values={{
                            countWord: intl.formatMessage(countWord),
                            topic,
                            bold: chuck => <strong>{chuck}</strong>,
                        }}
                    />
                </p>
                <dl
                    className={cn(
                        dibsCss.bgMoonstone,
                        dibsCss.grid,
                        styles.priceStats,
                        ...(isDesktop
                            ? [dibsCss.pMedium, dibsCss.mbSmaller]
                            : [dibsCss.pSmaller, dibsCss.mbXsmall])
                    )}
                >
                    <SbSharedMarketTrendsPriceStatsDescription
                        descriptionTerm={intl.formatMessage(priceStatsMessages.itemsSold)}
                        tooltipText={intl.formatMessage(priceStatsMessages.itemsSoldTooltip)}
                        className={cn(
                            dibsCss.pbSmaller,
                            dibsCss.prXsmall,
                            dibsCss.borderR,
                            dibsCss.mrXsmall,
                            dibsCss.borderRSolid,
                            dibsCss.borderDolphin
                        )}
                    >
                        {itemsSold}
                    </SbSharedMarketTrendsPriceStatsDescription>

                    <SbSharedMarketTrendsPriceStatsDescription
                        descriptionTerm={intl.formatMessage(priceStatsMessages.averageSalePrice)}
                        tooltipText={intl.formatMessage(priceStatsMessages.averageSalePriceTooltip)}
                        className={cn(dibsCss.pbSmaller, dibsCss.mlXsmall)}
                    >
                        <FormattedNumber
                            value={avgSalePriceAmount}
                            style="currency"
                            currency={currency}
                            maximumFractionDigits={0}
                        />
                    </SbSharedMarketTrendsPriceStatsDescription>

                    <SbSharedMarketTrendsPriceStatsDescription
                        descriptionTerm={intl.formatMessage(priceStatsMessages.priceRange)}
                        tooltipText={intl.formatMessage(priceStatsMessages.priceRangeTooltip)}
                        className={cn(
                            dibsCss.ptSmall,
                            dibsCss.borderT,
                            dibsCss.borderTSolid,
                            dibsCss.mrXsmall,
                            dibsCss.prXsmall,
                            dibsCss.borderR,
                            dibsCss.borderRSolid,
                            dibsCss.borderDolphin
                        )}
                    >
                        <FormattedNumber
                            value={priceRangeLowAmount}
                            style="currency"
                            currency={currency}
                            maximumFractionDigits={0}
                        />
                        {' – '}
                        <FormattedNumber
                            value={priceRangeHighAmount}
                            style="currency"
                            currency={currency}
                            maximumFractionDigits={0}
                        />
                    </SbSharedMarketTrendsPriceStatsDescription>

                    <SbSharedMarketTrendsPriceStatsDescription
                        descriptionTerm={intl.formatMessage(priceStatsMessages.valueChange)}
                        tooltipText={intl.formatMessage(priceStatsMessages.valueChangeTooltip)}
                        className={cn(
                            dibsCss.ptSmall,
                            dibsCss.plSmall,
                            styles.negativeLeftMargin,
                            dibsCss.borderT,
                            dibsCss.borderTSolid,
                            dibsCss.borderDolphin
                        )}
                    >
                        {`${valueChange}%`}
                    </SbSharedMarketTrendsPriceStatsDescription>
                </dl>
                <p className={cn(dibsCss.sassyFontBodySmall, dibsCss.textSatan)}>
                    <small className={styles.source}>
                        <FormattedMessage
                            id="abf.SbSharedMarketTrends.priceStats.source"
                            defaultMessage="Source: Analysis of proprietary sales data from 1stDibs.com."
                        />
                    </small>
                </p>
                <SbSharedMarketTrendsIconicDesigns
                    topic={topic}
                    creatorMarketTrends={creatorMarketTrends}
                />
            </>
        </SbSharedMarketTrendsQuestionWrapper>
    );
};
